import React, { useState, useRef, useCallback, useEffect } from "react";
import { cn } from "../../utils/classMerger";

interface TooltipProps {
    message: string;
    visible: boolean;
    children: React.ReactNode;
    className?: string;
    tooltipClassName?: string;
    arrowClassName?: string;
    showArrow?: boolean;
}

const VIEWPORT_PADDING = 8;

const SimpleTooltip: React.FC<TooltipProps> = ({
    message,
    children,
    visible,
    className,
    tooltipClassName,
    arrowClassName,
    showArrow = false,
}) => {
    const [hovered, setHovered] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const anchorRect = useRef<DOMRect | null>(null);

    const handleMouseEnter = useCallback(() => {
        if (!visible || !containerRef.current) return;
        anchorRect.current = containerRef.current.getBoundingClientRect();
        setHovered(true);
    }, [visible]);

    const handleMouseLeave = useCallback(() => {
        setHovered(false);
    }, []);

    // Dismiss tooltip on scroll so it doesn't float away from its anchor
    useEffect(() => {
        if (!hovered) return;
        const dismiss = () => setHovered(false);
        window.addEventListener("scroll", dismiss, true);
        return () => window.removeEventListener("scroll", dismiss, true);
    }, [hovered]);

    // Ref callback: measures the tooltip and positions it, clamped to the
    // anchor's dialog when it has one, otherwise to the viewport.
    const positionTooltip = useCallback((el: HTMLDivElement | null) => {
        if (!el || !anchorRect.current) return;
        const rect = anchorRect.current;

        // MB-1430: the tooltip is `position: fixed`, so inside a modal it
        // escapes the dialog and a long message visibly overhangs its edge.
        // A dialog ancestor is the real boundary; the viewport is the
        // fallback for the many anchors that aren't in one.
        const boundaryEl = containerRef.current?.closest(
            '[data-tooltip-boundary], [role="dialog"]'
        );
        const bounds = boundaryEl?.getBoundingClientRect();
        const minLeft = Math.max(
            VIEWPORT_PADDING,
            (bounds?.left ?? 0) + VIEWPORT_PADDING
        );
        const maxRight = Math.min(
            window.innerWidth - VIEWPORT_PADDING,
            (bounds?.right ?? window.innerWidth) - VIEWPORT_PADDING
        );
        const available = Math.max(0, maxRight - minLeft);

        // Narrow the bubble to whichever is tighter: the width its classes
        // allow, or the space the boundary leaves. Clearing the inline value
        // first keeps the class-derived cap readable on a re-measure.
        const bubble = el.querySelector<HTMLElement>("[data-tooltip-bubble]");
        if (bubble) {
            bubble.style.maxWidth = "";
            const classMax = parseFloat(
                window.getComputedStyle(bubble).maxWidth
            );
            bubble.style.maxWidth = `${
                Number.isNaN(classMax) ? available : Math.min(classMax, available)
            }px`;
        }

        // Measure the bubble, not the wrapper: the wrapper is a fixed,
        // shrink-to-fit box still sitting at left:0, so it reports the full
        // available width whenever the message is longer than one screen.
        const tipWidth = bubble?.offsetWidth ?? el.offsetWidth;
        const tipHeight = bubble?.offsetHeight ?? el.offsetHeight;

        let left = rect.left + rect.width / 2 - tipWidth / 2;
        left = Math.max(minLeft, Math.min(left, maxRight - tipWidth));

        // A wrapped message is tall enough to clear the top of the screen on
        // a phone - flip it under the anchor rather than off-screen.
        let top = rect.top - tipHeight - 4;
        const flipped = top < VIEWPORT_PADDING;
        if (flipped) {
            top = Math.min(
                rect.bottom + 4,
                window.innerHeight - tipHeight - VIEWPORT_PADDING
            );
        }

        // The arrow is drawn pointing down from the bubble's underside; below
        // the anchor it would point the wrong way, and its colour comes from
        // a caller-supplied border-t-* class that can't be rotated generically.
        const arrow = el.querySelector<HTMLElement>("[data-tooltip-arrow]");
        if (arrow) arrow.style.display = flipped ? "none" : "";

        el.style.left = `${left}px`;
        el.style.top = `${Math.max(VIEWPORT_PADDING, top)}px`;
        el.style.visibility = 'visible';
    }, []);

    return (
        <div
            ref={containerRef}
            className={cn("relative inline-flex w-auto", className)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {children}
            {visible && hovered ? (
                <div
                    ref={positionTooltip}
                    className="pointer-events-none z-50"
                    style={{
                        position: 'fixed',
                        left: 0,
                        top: 0,
                        visibility: 'hidden',
                    }}
                >
                    <div
                        data-tooltip-bubble
                        className={cn(
                        "text-xs font-semibold rounded py-1 px-2 leading-snug w-max max-w-[calc(100vw-1rem)] min-[640px]:max-w-xs",
                        tooltipClassName || "bg-secondary text-primary"
                    )}>
                        {message}
                    </div>
                    {showArrow && (
                        <div
                            data-tooltip-arrow
                            className={cn(
                            "absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-t-4",
                            arrowClassName || "border-t-secondary"
                        )} />
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default SimpleTooltip;
